import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { articleService } from '@/services/article';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useSeoFields } from '@/hooks/article-editor/useSeoFields';
import { useArticleOperations } from '@/hooks/article-editor/useArticleOperations';
import { DEFAULT_ARTICLE_METADATA } from '@/hooks/article-editor/constants';
import { getFirstParagraphText } from '@/hooks/article-editor/utils';

interface UseArticleEditorProps {
  articleId?: string;
}

export const useArticleEditor = ({ articleId }: UseArticleEditorProps = {}) => {
  const [article, setArticle] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [lead, setLead] = useState('');
  const [content, setContent] = useState('');
  const [editorData, setEditorData] = useState<any>({ blocks: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [metadata, setMetadata] = useState<Record<string, any>>({});
  
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const currentArticleId = articleId || id;
  const navigate = useNavigate();
  
  const {
    isSaving,
    isPublishing,
    lastSaved,
    saveArticle: executeSave,
    publishArticle: executePublish
  } = useArticleOperations({
    articleId: currentArticleId,
    isNew,
    user
  });
  
  const {
    seoTitle,
    seoDescription,
    updateSeoTitle,
    updateSeoDescription
  } = useSeoFields({
    title,
    editorData,
    initialSeoTitle: metadata.seoTitle,
    initialSeoDescription: metadata.seoDescription
  });

  useEffect(() => {
    if (seoTitle !== metadata.seoTitle) {
      updateMetadata('seoTitle', seoTitle);
    }
  }, [seoTitle]);

  useEffect(() => {
    if (seoDescription !== metadata.seoDescription) {
      updateMetadata('seoDescription', seoDescription);
    }
  }, [seoDescription]);

  useEffect(() => {
    const fetchArticle = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!currentArticleId || currentArticleId === 'new') {
          setIsNew(true);
          setArticle({
            title: '',
            subtitle: '',
            content: '',
            lead: '',
            status: 'draft',
          });
          setTitle('');
          setLead('');
          setContent('');
          setEditorData({ blocks: [] });
          setMetadata(DEFAULT_ARTICLE_METADATA);
          setIsLoading(false);
          return;
        }
        
        const { data, error } = await articleService.getArticleById(currentArticleId);
        if (error) {
          setError(error);
          toast({
            title: "Error",
            description: "Failed to load article: " + (error.message || "Unknown error"),
            variant: "destructive"
          });
        } else {
          console.log("Article data loaded:", data);
          setArticle(data);
          setTitle(data.title || '');
          setLead(data.lead || '');
          
          if (data.blocks && data.blocks.length > 0) {
            setContent(data.content || '');
            setEditorData(data.editor_data || { blocks: [] });
          } else if (typeof data.content === 'string') {
            setContent(data.content);
            setEditorData({ 
              blocks: [{ type: "paragraph", data: { text: data.content } }] 
            });
          } else {
            setContent(articleService.extractPlainTextFromBlocks(data.content?.blocks || []));
            setEditorData(data.content || { blocks: [] });
          }
          
          setMetadata({
            excerpt: data.excerpt,
            subtitle: data.subtitle,
            readingTime: data.reading_time || data.readingTime || 0,
            tags: data.tags || [],
            featuredImage: data.featured_image_url || data.featuredImageUrl,
            featuredImageAlt: data.featured_image_alt || data.featuredImageAlt,
            headerImage: data.header_image_url,
            paymentType: data.payment_type || data.paymentType || 'free',
            price: data.micropayment_price || data.price,
            isPaywalled: data.is_paywalled || data.isPaywalled || false,
            seoTitle: data.seo_title || data.seoTitle || data.title || '',
            seoDescription: data.seo_description || data.seoDescription || 
              getFirstParagraphText(data.content?.blocks || []),
            language: data.language || 'en',
            commentsEnabled: data.comments_enabled !== false,
            socialSharingEnabled: data.social_sharing_enabled !== false
          });
        }
      } catch (err: any) {
        setError(err);
        toast({
          title: "Error",
          description: err.message || "Failed to load article",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [currentArticleId]);

  const updateMetadata = (field: string, value: any) => {
    setMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveArticle = async () => {
    console.log("saveArticle called, title:", title, "lead:", lead, "currentArticleId:", currentArticleId);
    
    const articleData = {
      title,
      lead,
      content: editorData,
      excerpt: metadata.excerpt,
      subtitle: metadata.subtitle,
      read_time: metadata.readingTime,
      featured_image_url: metadata.featuredImage,
      featured_image_alt: metadata.featuredImageAlt,
      header_image_url: metadata.headerImage,
      payment_type: metadata.paymentType,
      micropayment_price: metadata.price,
      is_paywalled: metadata.isPaywalled,
      seo_title: metadata.seoTitle,
      seo_description: metadata.seoDescription,
      language: metadata.language,
      tags: metadata.tags
    };

    console.log("Saving article with data:", articleData);
    const result = await executeSave(articleData);
    console.log("Save result:", result);
    
    if (result && isNew) {
      setIsNew(false);
      if (typeof result === 'string') {
        navigate(`/dashboard/editor/${result}`);
      }
    }
    
    if (result) {
      setArticle((prevArticle: any) => ({ ...prevArticle, ...articleData }));
    }
    
    return result;
  };

  const publishArticle = async () => {
    console.log("publishArticle called, currentArticleId:", currentArticleId);
    
    const saveResult = await saveArticle();
    if (!saveResult) {
      toast({
        title: "Save Failed",
        description: "Article must be saved before publishing",
        variant: "destructive"
      });
      return false;
    }
    
    const result = await executePublish(currentArticleId);
    console.log("Publish result:", result);
    
    if (result) {
      setArticle((prevArticle: any) => ({ 
        ...prevArticle, 
        status: 'published', 
        published_at: new Date().toISOString() 
      }));
    }
    
    return result;
  };

  return {
    article,
    title,
    setTitle,
    lead,
    setLead,
    content,
    setContent,
    editorData,
    setEditorData,
    isLoading,
    error,
    isSaving,
    isPublishing,
    lastSaved,
    isNew,
    metadata,
    updateMetadata,
    saveArticle,
    publishArticle,
  };
};
